// © 2015-2021 WDNA - All Rights Reserved

import _ from 'lodash';
import * as React from 'react';
import DatePicker from 'react-datepicker';

import Select2, { DataSelect2 } from 'wdna-select2';

import { LovDO } from '../../../configuration/requests';
import { SensorDO, WasteInfoDO, WasteContainerDO } from '../../requests';

import './assets/react-datepicker.css';

interface Props {
  formValues: {
    isNew: boolean;
    dtype?: WasteContainerDO;
    data?: Partial<WasteContainerDO>;
    dinfo?: WasteInfoDO;
    info?: Partial<WasteInfoDO>;
    sensorArray?: SensorDO[];
    modelWasteArray?: LovDO[];
    typeWasteArray?: LovDO[];
    wasteInfoTable?: WasteInfoDO[];
  };
  duplicate: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | Date | null) => void;
  onChangeInfo: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAutoFormMin: (data: WasteContainerDO) => number;
  onAutoFormMax: (data: WasteContainerDO) => number;
}

/**
 * Function to generate a form component
 *
 * @param props properties of the component
 * @returns jsx element with the form
 */
function Form(props: Props): JSX.Element {
  const { sensorArray, modelWasteArray, typeWasteArray, isNew } = props.formValues;
  const data: WasteContainerDO = (props.formValues.dtype ? props.formValues.dtype : props.formValues.data) as WasteContainerDO;
  const info: WasteInfoDO = (props.formValues.dinfo ? props.formValues.dinfo : props.formValues.info) as WasteInfoDO;

  const timezoneOffset: any = {
    hours: Math.trunc(-data.date.getTimezoneOffset() / 60),
    minutes: -data.date.getTimezoneOffset() % 60,
  };
  // eslint-disable-next-line max-len
  const timezoneString: string = `UTC ${timezoneOffset.hours < 0 ? '-' : '+'}${timezoneOffset.hours.toString().padStart(2, '0')}:${timezoneOffset.minutes.toString().padStart(2, '0')}`;

  return (
    <fieldset>
      <div className="row">
        {sensorArray && (
          <div className="col-3">
            <label className="label">
              <b>Sensor</b> &nbsp;
            </label>
            <span className="mandatory">
              <Select2
                id="data.id"
                name="data.id"
                value={_.defaultTo(data.id, '')}
                onChange={props.onChange}
                data-ctype="number"
                data={sensorArray.map((sensor: SensorDO): DataSelect2 => {
                  return { text: `[${sensor.code}] ${sensor.name}`, id: sensor.id };
                })}
                options={{ placeholder: 'Select option' }}
                style={{ width: '80%' }}
                disabled={!isNew}
                required
              />
            </span>
          </div>
        )}
        <div className="col-3">
          <label className="label">
            <b>Type</b> &nbsp;
          </label>
          <span className="mandatory">
            <Select2
              id="data.type"
              name="data.type"
              value={_.defaultTo(data.type, '')}
              onChange={props.onChange}
              data={typeWasteArray?.map((item: LovDO): DataSelect2 => {
                return { id: item.id, text: item.name };
              })}
              options={{ placeholder: 'Select option' }}
              style={{ width: '80%' }}
              required
            />
          </span>
        </div>
        <div className="col-3">
          <label className="label">
            <b>Size</b> &nbsp;
          </label>
          <span className="mandatory">
            <Select2
              id="data.size"
              name="data.size"
              value={_.defaultTo(data.size, '')}
              onChange={props.onChange}
              data-ctype="number"
              data={[120, 240, 800, 1000]?.map((item: number): DataSelect2 => {
                return { id: item, text: String(item) };
              })}
              options={{ placeholder: 'Select option' }}
              style={{ width: '80%' }}
              required
            />
          </span>
        </div>
        <div className="col-3">
          <label className="label">
            <b>Model</b> &nbsp;
          </label>
          <span className="mandatory">
            <Select2
              id="data.model"
              name="data.model"
              value={_.defaultTo(data.model, '')}
              onChange={props.onChange}
              data={modelWasteArray?.map((item: LovDO): DataSelect2 => {
                return { id: item.id, text: item.name };
              })}
              options={{ placeholder: 'Select option' }}
              style={{ width: '80%' }}
              required
            />
          </span>
        </div>
      </div>
      <br />
      <div className="row">
        <div className="col-3" style={{ paddingRight: '0rem' }}>
          <label className="label">
            <b>MIN Unload</b> &nbsp;&nbsp;
          </label>
          <input
            type="number"
            id="data.minUnload"
            name="data.minUnload"
            value={data.minUnload || props.onAutoFormMin(data)}
            onChange={props.onChange}
            data-ctype="number"
            className="input-mandatory"
            required
            min={0}
          />
        </div>
        <div className="col-3" style={{ paddingRight: '0rem' }}>
          <label className="label">
            <b>MAX Weight</b> &nbsp;&nbsp;
          </label>
          <input
            type="number"
            id="data.maxWeight"
            name="data.maxWeight"
            value={data.maxWeight || props.onAutoFormMax(data)}
            onChange={props.onChange}
            data-ctype="number"
            className="input-mandatory"
            required
            min={0}
          />
        </div>
        <div className="col-3" style={{ paddingRight: '0rem' }}>
          <label className="label">
            <b>MIN Temp</b> &nbsp;&nbsp;
          </label>
          <input
            type="number"
            id="data.minTemp"
            name="data.minTemp"
            value={data.minTemp || '1'}
            onChange={props.onChange}
            data-ctype="number"
            className="input-mandatory"
            required
            min={0}
          />
        </div>
        <div className="col-3" style={{ paddingRight: '0rem' }}>
          <label className="label">
            <b>MAX Temp</b> &nbsp;&nbsp;
          </label>
          <input
            type="number"
            id="data.maxTemp"
            name="data.maxTemp"
            value={data.maxTemp || '50'}
            onChange={props.onChange}
            data-ctype="number"
            className="input-mandatory"
            required
            min={0}
          />
        </div>
      </div>
      <br />
      {(isNew || props.duplicate) && (
        <div id="info">
          <div className="row">
            <div className="col-3" style={{ paddingRight: '0rem' }}>
              <label className="label">
                <b>Ratio Wheel-1</b>
              </label>
              <input
                type="number"
                id="info.ratio1"
                name="info.ratio1"
                value={info.ratio1 || 0}
                onChange={props.onChangeInfo}
                data-ctype="number"
                className="input-mandatory"
                required
              />
            </div>
            <div className="col-3" style={{ paddingRight: '0rem' }}>
              <label className="label">
                <b>Ratio Wheel-2</b>
              </label>
              <input
                type="number"
                id="info.ratio2"
                name="info.ratio2"
                value={info.ratio2 || 0}
                onChange={props.onChangeInfo}
                data-ctype="number"
                className="input-mandatory"
                required
              />
            </div>
            <div className="col-3" style={{ paddingRight: '0rem' }}>
              <label className="label">
                <b>Ratio Wheel-3</b>
              </label>
              <input
                type="number"
                id="info.ratio3"
                name="info.ratio3"
                value={info.ratio3 || 0}
                onChange={props.onChangeInfo}
                data-ctype="number"
                className="input-mandatory"
                required
              />
            </div>
            <div className="col-3" style={{ paddingRight: '0rem' }}>
              <label className="label">
                <b>Ratio Wheel-4</b>
              </label>
              <input
                type="number"
                id="info.ratio4"
                name="info.ratio4"
                value={info.ratio4 || 0}
                onChange={props.onChangeInfo}
                data-ctype="number"
                className="input-mandatory"
                required
              />
            </div>
          </div>
          <br />
        </div>
      )}
      <div className="row">
        <div className="col-3" style={{ paddingRight: '0rem' }}>
          <label className="label">
            <b>Date of creation</b> &nbsp;
          </label>
          <i className="fa fa-calendar">
            &nbsp;
          </i>
          <DatePicker
            id="date"
            name="date"
            selected={data.date}
            onChange={props.onChange}
            className="input-new"
            showTimeSelect
            timeFormat="HH:mm:ss"
            timeIntervals={15}
            dateFormat={`yyyy-MM-dd HH:mm:ss '${timezoneString}'`}
            timeCaption="time"
            todayButton="TODAY"
            disabled={!isNew}
          />
          <label>&nbsp;&nbsp;{isNew ? '*' : ''}</label>
        </div>
        <div className="col-3" style={{ paddingRight: '0rem' }}>
          <label className="label">
            <b>Status</b>&nbsp;
          </label>
          <input
            type="checkbox"
            id="data.active"
            name="data.active"
            checked={data.active || false}
            onChange={props.onChange}
            data-ctype="boolean"
            className="input-new"
          />
          &nbsp;&nbsp;
          <label className="label" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
            Active
          </label>
        </div>
      </div>
    </fieldset>
  );
}

export default Form;
